import { useEffect, useState } from "react";

/**
 * Altura ocupada pelo banner de cookies no rodapé (0 quando não há banner).
 *
 * Quem chega por deep link de In-App Event está sempre na primeira visita, então
 * o banner de consentimento aparece em 100% desse tráfego e cobria a barra fixa
 * de CTA. Em vez de chutar um `bottom` fixo, medimos o banner de verdade — ele
 * muda de altura entre mobile (largura cheia) e desktop (card à direita).
 */
export function useCookieBannerOffset(): number {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    let raf = 0;
    let observed: HTMLElement | null = null;
    let ro: ResizeObserver | null = null;

    const medir = () => {
      const el = document.querySelector<HTMLElement>("[data-cookie-banner]");
      if (!el) {
        setOffset(0);
        return;
      }
      const rect = el.getBoundingClientRect();
      // Distância do topo do banner até a base da janela + respiro.
      setOffset(Math.max(0, window.innerHeight - rect.top + 8));

      if (observed !== el) {
        ro?.disconnect();
        observed = el;
        ro = new ResizeObserver(() => {
          raf = window.requestAnimationFrame(medir);
        });
        ro.observe(el);
      }
    };

    medir();
    // O banner monta/desmonta depois (consentimento é assíncrono): observa o DOM.
    const mo = new MutationObserver(() => {
      raf = window.requestAnimationFrame(medir);
    });
    mo.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", medir);

    return () => {
      window.cancelAnimationFrame(raf);
      mo.disconnect();
      ro?.disconnect();
      window.removeEventListener("resize", medir);
    };
  }, []);

  return offset;
}
